import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Plus, Pin, Lock, MessageSquare, ArrowLeft } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { MemberBar, fmtWhen } from "@/components/forum/MemberBar";
import { NewTopicDialog } from "@/components/forum/NewTopicDialog";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { memberApi } from "@/lib/api";

export default function ForumCategory() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { member } = useMemberAuth();
  const navigate = useNavigate();

  const load = () => {
    memberApi.get(`/forum/categories/${slug}`).then((r) => setCat(r.data)).catch(() => setNotFound(true));
    memberApi.get("/forum/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(load, [slug]);

  const onNew = () => {
    if (!member) return navigate("/forum/join?mode=register");
    setShowNew(true);
  };

  if (notFound) {
    return (
      <div className="pt-52 pb-28 text-center">
        <p className="text-brand-textSec">Board not found.</p>
        <Link to="/forum" className="text-brand-cyan mt-4 inline-block">Back to forum</Link>
      </div>
    );
  }

  return (
    <>
      <PageHeader label="Forum Board" title={(cat?.name || "").toUpperCase()}>
        {cat?.description}
      </PageHeader>

      <section className="pb-28" data-testid="forum-category">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link to="/forum" className="inline-flex items-center gap-2 text-brand-textSec hover:text-brand-cyan text-sm mb-6" data-testid="forum-back-link">
            <ArrowLeft size={16} /> All boards
          </Link>
          <MemberBar />

          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-bold text-2xl text-white">Topics</h2>
            <button onClick={onNew} disabled={!cat || categories.length === 0} className="btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="category-new-topic-btn">
              <Plus size={16} /> New Topic
            </button>
          </div>

          {cat && cat.topics.length === 0 ? (
            <div className="card-surface p-10 text-center">
              <MessageSquare size={28} className="mx-auto text-brand-textMuted" />
              <p className="text-brand-textSec mt-4">No topics here yet. Be the first to start one.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {cat?.topics.map((t) => (
                <Link key={t.id} to={`/forum/topic/${t.id}`} className="flex items-center justify-between gap-4 card-surface px-5 py-4" data-testid={`topic-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {t.pinned && <Pin size={13} className="text-brand-cyan shrink-0" />}
                      {t.locked && <Lock size={13} className="text-brand-textMuted shrink-0" />}
                      <p className="text-white font-medium truncate">{t.title}</p>
                    </div>
                    <p className="text-brand-textMuted text-xs mt-1">by {t.author_name} · {fmtWhen(t.created_at)}</p>
                  </div>
                  <span className="text-brand-textSec text-sm shrink-0">{t.reply_count} repl{t.reply_count === 1 ? "y" : "ies"}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {showNew && (
        <NewTopicDialog
          categories={categories}
          defaultCategoryId={cat?.id}
          onClose={() => setShowNew(false)}
          onCreated={(topic) => { setShowNew(false); navigate(`/forum/topic/${topic.id}`); }}
        />
      )}
    </>
  );
}
